import { useEffect, useCallback, useMemo } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { usePermissions } from "../../../hooks/usePermissions";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import { useNavigate, useLocation } from "react-router-dom";
import {
  faEdit,
  faTrashAlt,
  faUndo,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import ListHeader from "../../../components/ListHeader";
import useAction from "../../../hooks/useAction";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import AutoCompleteFilter from "../../../components/AutoCompleteFilter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import baseService from "../../../services/baseService";
import useFilterHandlers from "../../../hooks/useFilterHandlers";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import FilterCollapse from "../../../components/FilterCollapse";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const UnitPage = () => {
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate);

  // --- Padronização de filtros via useUrlFilters ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      name: [],
      abbreviation: [],
      deleted_at: false,
    },
    "equipments_units",
  );

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters);

  // Inicializa o useGenericFilter com o estado inicial dos filtros do formulário
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        name: [],
        abbreviation: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear,
    );

  // --- Lógica de busca de unidades com TanStack Query ---
  const {
    data: unitsData,
    isLoading: isLoadingUnits,
    isFetching: isFetchingUnits,
    isError: isErrorUnits,
    error: unitsError,
  } = useQueryWithLoader({
    queryKey: ["equipments_units", filters, pagination],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTranformed,
        page: pagination.page,
        perPage: pagination.perPage,
      };
      const response = await baseServiceGet(entities.units.get, params);
      return response.result;
    },
    staleTime: 60 * 1000,
    select: (data) => {
      const transformed = data.data.map((unit) => ({
        id: unit.id,
        name: unit.name,
        abbreviation: unit.abbreviation,
        isDeleted: !!unit.deleted_at,
      }));
      return {
        units: transformed,
        totalPages: data.last_page,
      };
    },
  });

  const { units = [], totalPages = 1 } = unitsData || {};

  const columns = [
    { key: "name", label: "Nome" },
    { key: "abbreviation", label: "Abreviação" },
  ];

  // --- Gerenciamento de Loader com base nos estados do TanStack Query ---
  useEffect(() => {
    if (isLoadingUnits || isFetchingUnits) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoadingUnits, isFetchingUnits, showLoader, hideLoader]);

  // --- Notificação de Erro para a Query de Unidades ---
  useEffect(() => {
    if (isErrorUnits) {
      console.error(
        "UnitPage: Erro ao carregar unidades (useQuery):",
        unitsError,
      );
      showNotification(
        "error",
        `Erro ao carregar unidades: ${
          unitsError.response?.data?.message || unitsError.message
        }`,
      );
    }
  }, [isErrorUnits, unitsError, showNotification]);

  // --- Função para Forçar o Refetch da Lista (invalida o cache) ---
  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["equipments_units", filters, pagination],
    });
  }, [filters, pagination, queryClient]);

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  // --- Nova mutação para ativar unidade ---
  const inactiveUnitMutation = useMutation({
    mutationFn: async (unitId) => {
      showLoader();
      const response = await baseServiceDelete(entities.units.delete(unitId));
      return response;
    },
    onSuccess: (res) => {
      if (res?.status === 200) refreshList();
    },
    onError: (err) => {
      console.error("onError ", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
      handleCancelConfirmation();
    },
  });

  const activateUnitMutation = inactiveUnitMutation;

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      // Limpa o estado para que a mensagem não apareça novamente ao navegar
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const actions = useMemo(
    () => [
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar unidades de medida",
        onClick: (unit) => navigate(`/aluguel/unidades/editar/${unit.id}`),
        show: (unit) =>
          !unit.isDeleted && canAccess("Atualizar unidades de medida"),
      },
      {
        id: "viewUnitsRelated",
        icon: faLink,
        title: "Ver unidades relacionadas",
        buttonClass: "btn-info",
        permission: "Listar unidades de medida relacionadas",
        onClick: (unit) =>
          navigate(
            `/aluguel/unidades/${unit.id}/unidades-relacionadas/associar`,
          ),
        show: (unit) =>
          !unit.isDeleted &&
          canAccess("Listar unidades de medida relacionadas"),
      },
      {
        id: "delete",
        icon: faTrashAlt,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir unidades de medida",
        onClick: (unit) =>
          showConfirmation(
            unit,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveUnitMutation.mutateAsync(unit.id);
              } catch (error) {
                console.error("Erro ao excluir unidade:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (unit) =>
          !unit.isDeleted && canAccess("Excluir unidades de medida"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-info",
        permission: "Excluir unidades de medida",
        onClick: (unit) =>
          showConfirmation(
            unit,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateUnitMutation.mutateAsync(unit.id);
              } catch (error) {
                console.error("Erro ao restaurar unidade:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (unit) =>
          unit.isDeleted && canAccess("Excluir unidades de medida"),
      },
    ],
    [
      navigate,
      canAccess,
      showConfirmation,
      inactiveUnitMutation,
      activateUnitMutation,
    ],
  );

  // Inputs para o formulário de filtros
  const inputsfilters = [
    {
      label: "Nome",
      gridSize: "6",
      component: (
        <AutoCompleteFilter
          service={baseService}
          columnDataBase="name"
          model="unit"
          value={localFilterValues.name}
          onChange={(selected) => handleFilterChange("name", selected)}
          placeholder="Filtre as unidades pelo nome"
          isMulti
          isClearable
        />
      ),
    },
    {
      label: "Abreviação",
      gridSize: "6",
      component: (
        <AutoCompleteFilter
          service={baseService}
          columnDataBase="abbreviation"
          model="unit"
          value={localFilterValues.abbreviation}
          onChange={(selected) => handleFilterChange("abbreviation", selected)}
          placeholder="Filtre pela abreviação"
          isMulti
          isClearable
        />
      ),
    },
  ];

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Unidades" showBackButton={true} backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de Unidades"
          buttonText="Nova Unidade"
          buttonLink="/aluguel/unidades/criar"
          canAccess={canAccess}
          permission="Criar unidades de medida"
        />

        <TableWithInactiveFilter
          data={units}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoadingUnits}
          isFetching={isFetchingUnits}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default UnitPage;
